<div class="page-header">
  <div class="page-title">
    @if (criteriaId || !isEditMode) {
      Lead Criteria Details
    } @else {
      Create Lead Criteria
    }
  </div>
  <div class="page-actions">
    <button
      type="button"
      class="back-button"
      (click)="onGoBack()"
      title="Back to Leads Criteria"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M19 12H5M12 19l-7-7 7-7"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      Back
    </button>

    @if (!isEditMode && savedCriteria) {
      <button
        type="button"
        class="edit-button"
        (click)="onEdit()"
        [disabled]="savedCriteria.deprecated"
      >
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
          <path
            d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25a1.75 1.75 0 01.445-.758L11.013 1.427z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M8.5 3.5l4 4"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Edit
      </button>
    }
  </div>
</div>

<!-- Loading State -->
@if (isLoading) {
  <div class="loading-container">
    <div class="loading-message">Loading lead criteria...</div>
  </div>
}

<!-- Content - Only show when not loading -->
@if (!isLoading) {
  <!-- Deprecated Warning Message -->
  @if (savedCriteria?.deprecated && !isEditMode) {
    <div class="deprecated-warning">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <div class="warning-content">
        <strong>This criteria is deprecated</strong>
        <p>
          This lead criteria has been marked as deprecated and cannot be edited.
          It is kept for reference purposes only.
        </p>
      </div>
    </div>
  }

  <!-- View Mode -->
  @if (!isEditMode && savedCriteria) {
    <div class="matching-criteria-container view-mode">
      <div class="view-sections">
        <!-- Name -->
        <div class="view-section">
          <h3 class="section-title">Name</h3>
          <div class="view-content">
            <div class="text-content">
              <span class="name-text">{{ savedCriteria.name }}</span>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="view-section">
          <h3 class="section-title">Description</h3>
          <div class="view-content">
            @if (savedCriteria.description) {
              <div class="text-content">
                <div
                  class="description-text"
                  [innerHTML]="savedCriteria.description"
                ></div>
              </div>
            } @else {
              <div class="tag-list">
                <span class="no-data">Not specified</span>
              </div>
            }
          </div>
        </div>

        <!-- Supported Languages -->
        <div class="view-section">
          <div class="section-title-with-badge">
            <h3 class="section-title">Supported Languages</h3>
            @if (isMandatoryField("preferred_languages")) {
              <span class="must-match-badge">Must Match</span>
            }
          </div>
          <div class="view-content">
            <div class="tag-list">
              @for (language of getPreferredLanguagesArray(); track $index) {
                <span class="view-tag">{{ language }}</span>
              }
              @if (getPreferredLanguagesArray().length === 0) {
                <span class="no-data">Not specified</span>
              }
            </div>
          </div>
        </div>

        <!-- Nationwide Support -->
        <div class="view-section">
          <h3 class="section-title">Do you provide services nationwide?</h3>
          <div class="view-content">
            <div class="tag-list">
              @if (savedCriteria.criteria.nationwide_support !== undefined) {
                <span
                  class="view-tag"
                  [class.yes-tag]="savedCriteria.criteria.nationwide_support"
                  [class.no-tag]="!savedCriteria.criteria.nationwide_support"
                >
                  {{ savedCriteria.criteria.nationwide_support ? "Yes" : "No" }}
                </span>
              } @else {
                <span class="no-data">Not specified</span>
              }
            </div>
          </div>
        </div>

        <!-- States of Service -->
        @if (!savedCriteria.criteria.nationwide_support) {
          <div class="view-section">
            <div class="section-title-with-badge">
              <h3 class="section-title">States of Service</h3>
              @if (isMandatoryField("states")) {
                <span class="must-match-badge">Must Match</span>
              }
            </div>
            <div class="view-content">
              <div class="tag-list">
                @for (
                  state of savedCriteria.criteria.states || [];
                  track $index
                ) {
                  <span class="view-tag">{{ state }}</span>
                }

                @if ((savedCriteria.criteria.states || []).length === 0) {
                  <span class="no-data">Not specified</span>
                }
              </div>
            </div>
          </div>
        }
        <!-- Counties of Service -->
        @if (!savedCriteria.criteria.nationwide_support) {
          <div class="view-section">
            <div class="section-title-with-badge">
              <h3 class="section-title">Counties of Service</h3>
              @if (isMandatoryField("counties")) {
                <span class="must-match-badge">Must Match</span>
              }
            </div>
            <div class="view-content">
              <div class="tag-list">
                @for (
                  county of savedCriteria.criteria.counties || [];
                  track $index
                ) {
                  <span class="view-tag">{{ county }}</span>
                }
                @if ((savedCriteria.criteria.counties || []).length === 0) {
                  <span class="no-data">Not specified</span>
                }
              </div>
            </div>
          </div>
        }

        <!-- Cities -->
        @if (!savedCriteria.criteria.nationwide_support) {
          <div class="view-section">
            <div class="section-title-with-badge">
              <h3 class="section-title">Specific Cities / Townships</h3>
              @if (isMandatoryField("cities")) {
                <span class="must-match-badge">Must Match</span>
              }
            </div>
            <div class="view-content">
              <div class="tag-list">
                @for (
                  city of savedCriteria.criteria.cities || [];
                  track $index
                ) {
                  <span class="view-tag">{{ formatCityName(city) }}</span>
                }
                @if ((savedCriteria.criteria.cities || []).length === 0) {
                  <span class="no-data">Not specified</span>
                }
              </div>
            </div>
          </div>
        }

        <!-- Zip Codes -->
        @if (!savedCriteria.criteria.nationwide_support) {
          <div class="view-section">
            <div class="section-title-with-badge">
              <h3 class="section-title">Zip Codes</h3>
              @if (isMandatoryField("zip_codes")) {
                <span class="must-match-badge">Must Match</span>
              }
            </div>
            <div class="view-content">
              <div class="tag-list">
                @for (
                  zipCode of savedCriteria.criteria.zip_codes || [];
                  track $index
                ) {
                  <span class="view-tag zip-code">{{ zipCode }}</span>
                }
                @if ((savedCriteria.criteria.zip_codes || []).length === 0) {
                  <span class="no-data">Not specified</span>
                }
              </div>
            </div>
          </div>
        }

        <!--Military Program -->
        <div class="view-section">
          <div class="section-title-with-badge">
            <h3 class="section-title">
              Is this program for US Military Veterans?
            </h3>
            @if (isMandatoryField("is_military_veteran_prog")) {
              <span class="must-match-badge">Must Match</span>
            }
          </div>
          <div class="view-content">
            <div class="text-content">
              <span class="name-text">
                {{
                  savedCriteria.criteria.is_military_veteran_prog ? "Yes" : "No"
                }}
              </span>
            </div>
          </div>
        </div>

        <!--Military Veteran Spouse-->
        <div class="view-section">
          <div class="section-title-with-badge">
            <h3 class="section-title">
              Is this program for spouses of US military veterans?
            </h3>
            @if (isMandatoryField("is_veteran_spouse_prog")) {
              <span class="must-match-badge">Must Match</span>
            }
          </div>
          <div class="view-content">
            <div class="text-content">
              <span class="name-text">
                {{
                  savedCriteria.criteria.is_veteran_spouse_prog ? "Yes" : "No"
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Business Support -->
        <div class="view-section">
          <div class="section-title-with-badge">
            <h3 class="section-title">Business Support Options</h3>
            @if (isMandatoryField("supports")) {
              <span class="must-match-badge">Must Match</span>
            }
          </div>
          <div class="view-content">
            <div class="tag-list">
              @for (support of getSupportsArray(); track $index) {
                <span class="view-tag">{{ support }}</span>
              }
              @if (getSupportsArray().length === 0) {
                <span class="no-data">Not specified</span>
              }
            </div>
          </div>
        </div>

        <!-- Business Entity Requirement -->
        <div class="view-section">
          <div class="section-title-with-badge">
            <h3 class="section-title">
              Does there need to be a business entity to lend or deploy capital
              to?
            </h3>
            @if (isMandatoryField("need_business_entity")) {
              <span class="must-match-badge">Must Match</span>
            }
          </div>
          <div class="view-content">
            <div class="text-content">
              <span class="name-text">
                {{ savedCriteria.criteria.need_business_entity ? "Yes" : "No" }}
              </span>
            </div>
          </div>
        </div>

        <!-- Loans and Capital Products Section - Only shown when selected -->
        @if (savedCriteria.criteria.supports.includes("loans_and_capital")) {
          <div class="loans-capital-section">
            <div class="section-header-main">
              <h3 class="section-title-main">Loans and Capital Products</h3>
            </div>
            <div class="section-content">
              <!-- State Registration Requirement -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Does the business need to have a state registration?
                  </h3>
                  @if (
                    isMandatoryField(
                      "loans_and_capital.need_state_registration"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      {{
                        savedCriteria.criteria.loans_and_capital
                          ?.need_state_registration
                          ? "Yes"
                          : "No"
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Co-signer Requirement -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Does a co-signer help the prospect of a loan deployment?
                  </h3>
                  @if (isMandatoryField("loans_and_capital.cosigner_helps")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      @if (
                        savedCriteria.criteria.loans_and_capital
                          ?.cosigner_helps === "yes"
                      ) {
                        Yes
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.cosigner_helps === "no"
                      ) {
                        No
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.cosigner_helps === "not_required"
                      ) {
                        Not Required
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </span>
                  </div>
                </div>
              </div>

              <!-- Business Plan Requirement -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Does an applicant need a business plan for this product?
                  </h3>
                  @if (
                    isMandatoryField("loans_and_capital.need_business_plan")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      @if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_business_plan === "yes"
                      ) {
                        Yes
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_business_plan === "no"
                      ) {
                        No
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_business_plan === "yes_we_can_help"
                      ) {
                        Yes, and we can help create one for an applicant
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </span>
                  </div>
                </div>
              </div>

              <!-- Financial Projections Requirement -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you need financial projections for this product?
                  </h3>
                  @if (
                    isMandatoryField(
                      "loans_and_capital.need_financial_projections"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      @if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_financial_projections === "yes"
                      ) {
                        Yes
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_financial_projections === "no"
                      ) {
                        No
                      } @else if (
                        savedCriteria.criteria.loans_and_capital
                          ?.need_financial_projections === "yes_we_can_help"
                      ) {
                        Yes, and we can help create one for an applicant
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </span>
                  </div>
                </div>
              </div>

              <!-- Area Types -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">Area Types for Fund Deployment</h3>
                  @if (
                    isMandatoryField(
                      "loans_and_capital.capital_deployment_areas"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @for (
                      areaType of getCapitalDeploymentAreasArray();
                      track $index
                    ) {
                      <span class="view-tag">{{ areaType }}</span>
                    }
                    @if (getCapitalDeploymentAreasArray().length === 0) {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Loan Types -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">Loan Types</h3>
                  @if (isMandatoryField("loans_and_capital.loan_types")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @for (loanType of getLoanTypesArray(); track $index) {
                      <span class="view-tag">{{ loanType }}</span>
                    }
                    @if (getLoanTypesArray().length === 0) {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Loan Amount Range -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">Loan Amount Range</h3>
                  @if (isMandatoryField("loans_and_capital.loan_size_range")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="loan-range-display">
                    @if (
                      savedCriteria.criteria.loans_and_capital?.loan_size_range
                    ) {
                      <div class="loan-amounts">
                        <span class="amount-label">Minimum:</span>
                        <span class="amount-value"
                          >${{
                            savedCriteria.criteria.loans_and_capital
                              ?.loan_size_range?.min | number: "1.0-0"
                          }}</span
                        >
                        <span class="amount-separator">-</span>
                        <span class="amount-label">Maximum:</span>
                        <span class="amount-value"
                          >${{
                            savedCriteria.criteria.loans_and_capital
                              ?.loan_size_range?.max | number: "1.0-0"
                          }}</span
                        >
                      </div>
                    } @else {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Financial Documentation -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Financial Documentation Requirements
                  </h3>
                  @if (
                    isMandatoryField(
                      "loans_and_capital.financial_documentations"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @for (
                      doc of getFinancialDocumentationsArray();
                      track $index
                    ) {
                      <span class="view-tag">{{ doc }}</span>
                    }
                    @if (getFinancialDocumentationsArray().length === 0) {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Credit Score Ranges -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">Supported Credit Score Ranges</h3>
                  @if (
                    isMandatoryField("loans_and_capital.credit_score_range")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @if (getCreditScoreRange()) {
                      <span class="view-tag credit-range">{{
                        getCreditScoreRange()
                      }}</span>
                    } @else {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Small Business Technical Assistance Section - Only shown when selected -->
        @if (savedCriteria.criteria.supports.includes("technical_assistance")) {
          <div class="loans-capital-section">
            <div class="section-header-main">
              <h3 class="section-title-main">
                Small Business Technical Assistance
              </h3>
            </div>
            <div class="section-content">
              <!-- Target Industries Question -->
              <div class="view-section">
                <h3 class="section-title">
                  Do you have any target or preferred business industries?
                </h3>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      {{
                        savedCriteria.criteria.technical_assistance
                          ?.has_target_industries
                          ? "Yes"
                          : "No"
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Target Industries List - Only shown when Yes -->
              @if (
                savedCriteria.criteria.technical_assistance
                  ?.has_target_industries &&
                savedCriteria.criteria.technical_assistance?.target_industries
              ) {
                <div class="view-section">
                  <div class="section-title-with-badge">
                    <h3 class="section-title">Target Industries</h3>
                    @if (
                      isMandatoryField("technical_assistance.target_industries")
                    ) {
                      <span class="must-match-badge">Must Match</span>
                    }
                  </div>
                  <div class="view-content">
                    <div class="tag-list">
                      @for (
                        industryCode of savedCriteria.criteria
                          .technical_assistance?.target_industries;
                        track $index
                      ) {
                        @for (
                          industry of industryOptions;
                          track industry.value
                        ) {
                          @if (industry.value === industryCode) {
                            <span class="view-tag">{{ industry.label }}</span>
                          }
                        }
                      }
                      @if (
                        !savedCriteria.criteria.technical_assistance
                          ?.target_industries ||
                        savedCriteria.criteria.technical_assistance
                          ?.target_industries?.length === 0
                      ) {
                        <span class="no-data">Not specified</span>
                      }
                    </div>
                  </div>
                </div>
              }

              <!-- Support Areas -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">Small Business Support Areas</h3>
                  @if (isMandatoryField("technical_assistance.support_areas")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @if (getSupportAreasArray().length > 0) {
                      @for (area of getSupportAreasArray(); track $index) {
                        <span class="view-tag">{{ area }}</span>
                      }
                    } @else {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- Employee Ownership - Only shown when Business Succession Planning is selected -->
              @if (
                savedCriteria.criteria.technical_assistance?.support_areas?.includes(
                  "business_succession"
                )
              ) {
                <div class="view-section">
                  <h3 class="section-title">
                    Do you offer programming in Employee Ownership transitions?
                  </h3>
                  <div class="view-content">
                    <div class="text-content">
                      <span class="name-text">
                        {{
                          savedCriteria.criteria.technical_assistance
                            ?.offers_employee_ownership
                            ? "Yes"
                            : "No"
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              }

              <!-- Business Help Areas -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Can you help a business with any of the following?
                  </h3>
                  @if (
                    isMandatoryField("technical_assistance.business_help_areas")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="tag-list">
                    @if (getBusinessHelpAreasArray().length > 0) {
                      @for (area of getBusinessHelpAreasArray(); track $index) {
                        <span class="view-tag">{{ area }}</span>
                      }
                    } @else {
                      <span class="no-data">Not specified</span>
                    }
                  </div>
                </div>
              </div>

              <!-- TA Programs Paid Service -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Are your technical assistance programs a paid service?
                  </h3>
                  @if (
                    isMandatoryField(
                      "technical_assistance.ta_programs_paid_service"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <div class="text-content">
                    <span class="name-text">
                      @if (
                        savedCriteria.criteria.technical_assistance
                          ?.ta_programs_paid_service === "yes"
                      ) {
                        Yes
                      } @else if (
                        savedCriteria.criteria.technical_assistance
                          ?.ta_programs_paid_service === "no"
                      ) {
                        No
                      } @else if (
                        savedCriteria.criteria.technical_assistance
                          ?.ta_programs_paid_service === "some"
                      ) {
                        Some of them
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </span>
                  </div>
                </div>
              </div>

              <!-- Paid Support Areas - Only shown when Some of them is selected -->
              @if (
                savedCriteria.criteria.technical_assistance
                  ?.ta_programs_paid_service === "some"
              ) {
                <div class="view-section">
                  <h3 class="section-title">
                    Which services do you offer that are paid?
                  </h3>
                  <div class="view-content">
                    <div class="tag-list">
                      @if (getPaidSupportAreasArray().length > 0) {
                        @for (
                          area of getPaidSupportAreasArray();
                          track $index
                        ) {
                          <span class="view-tag">{{ area }}</span>
                        }
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Business Operational Tools Section - Only shown when selected -->
        @if (
          savedCriteria.criteria.supports.includes("business_operational_tools")
        ) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Business Operational Tools
              </h3>

              <!-- Operational Tools Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Does your organization offer any of the following tools or
                    services?
                  </h3>
                  @if (isMandatoryField("business_operational_tools.tools")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                @if (getOperationalToolsCategorized().length > 0) {
                  @for (
                    category of getOperationalToolsCategorized();
                    track $index
                  ) {
                    <div class="view-content" style="margin-bottom: 16px">
                      <h4
                        class="checkbox-group-header"
                        style="
                          margin-bottom: 8px;
                          font-weight: 500;
                          font-size: 13px;
                        "
                      >
                        {{ category.category }}
                      </h4>
                      <div class="tag-list">
                        @for (tool of category.tools; track tool) {
                          <span class="view-tag">{{ tool }}</span>
                        }
                      </div>
                    </div>
                  }
                } @else {
                  <div class="view-content">
                    <span class="no-data">Not specified</span>
                  </div>
                }
              </div>

              @if (
                operationalToolsArray.hasError("atLeastOneSelected") &&
                operationalToolsArray.touched
              ) {
                <div class="error-message">
                  Please select at least one operational tool of service
                </div>
              }
            </div>
          </div>
        }

        <!-- Commercial Real Estate Section - Only shown when selected -->
        @if (
          savedCriteria.criteria.supports.includes("commercial_real_estate")
        ) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Commercial Real Estate
              </h3>

              <!-- CRE Questions -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you help locate CRE facilities for a business to purchase
                    or rent?
                  </h3>
                  @if (
                    isMandatoryField(
                      "commercial_real_estate.cre_help_locate_facilities"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.commercial_real_estate
                        ?.cre_help_locate_facilities
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you have a product or service that helps a business
                    upgrade its commercial facility?
                  </h3>
                  @if (
                    isMandatoryField(
                      "commercial_real_estate.cre_upgrade_facility"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.commercial_real_estate
                        ?.cre_upgrade_facility
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Does the business need to own their facility to receive your
                    product/service?
                  </h3>
                  @if (
                    isMandatoryField(
                      "commercial_real_estate.cre_need_own_facility"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.commercial_real_estate
                        ?.cre_need_own_facility
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you work with tenants to connect with building ownership
                    to improve/upgrade the building?
                  </h3>
                  @if (
                    isMandatoryField(
                      "commercial_real_estate.cre_work_with_tenants"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.commercial_real_estate
                        ?.cre_work_with_tenants
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you offer building Solar or Energy Efficiency upgrades
                    products or services?
                  </h3>
                  @if (
                    isMandatoryField(
                      "commercial_real_estate.cre_solar_energy_upgrades"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.commercial_real_estate
                        ?.cre_solar_energy_upgrades
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Solar and Energy Efficiency Products Section - Only shown when selected -->
        @if (savedCriteria.criteria.supports.includes("solar_and_energy")) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h2 class="section-header-main">
                Solar and Energy Efficiency Products
              </h2>

              <!-- Solar Lending Products Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you offer Solar Lending Products or Solar Support
                    Services?
                  </h3>
                  @if (
                    isMandatoryField("solar_and_energy.solar_lending_products")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.solar_and_energy
                        ?.solar_lending_products
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <!-- Loan Amount + Solar Credit Score - Only shown when Solar Lending is Yes -->
              @if (
                savedCriteria.criteria.solar_and_energy?.solar_lending_products
              ) {
                <div class="followup-questions-container">
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.solar_loan_amount_range"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <div class="loan-range-display">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.solar_loan_amount_range
                        ) {
                          <div class="loan-amounts">
                            <span class="amount-label">Minimum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.solar_loan_amount_range?.min
                                  | number: "1.0-0"
                              }}</span
                            >
                            <span class="amount-separator">-</span>
                            <span class="amount-label">Maximum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.solar_loan_amount_range?.max
                                  | number: "1.0-0"
                              }}</span
                            >
                          </div>
                        } @else {
                          <span class="no-data">Not specified</span>
                        }
                      </div>
                    </div>
                  </div>

                  <!-- Solar Credit Score Question (inside Solar Lending followup) -->
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.solar_need_credit_score"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <span class="name-text">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.solar_need_credit_score
                        ) {
                          Yes
                        } @else {
                          No
                        }
                      </span>
                    </div>
                  </div>

                  <!-- Solar Credit Score Ranges - Only shown when Credit Score is Yes -->
                  @if (
                    savedCriteria.criteria.solar_and_energy
                      ?.solar_need_credit_score
                  ) {
                    <div class="followup-questions-container">
                      <div class="view-section">
                        <div class="section-title-with-badge">
                          <h3 class="section-title">Credit score range:</h3>
                          @if (
                            isMandatoryField(
                              "solar_and_energy.solar_credit_score_ranges"
                            )
                          ) {
                            <span class="must-match-badge">Must Match</span>
                          }
                        </div>
                        <div class="view-content">
                          <div class="tag-list">
                            @if (getSolarCreditScoreRange()) {
                              <span class="view-tag">{{
                                getSolarCreditScoreRange()
                              }}</span>
                            } @else {
                              <span class="no-data">Not specified</span>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- Solar Loan Requirements Question -->
                  <div class="view-section">
                    <h3 class="section-title">
                      Do you require anything else for a Solar Loan?
                    </h3>
                    <div class="view-content">
                      @if (
                        savedCriteria.criteria.solar_and_energy
                          ?.solar_loan_requirements
                      ) {
                        <span class="name-text">
                          {{
                            savedCriteria.criteria.solar_and_energy
                              ?.solar_loan_requirements
                          }}
                        </span>
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </div>
                  </div>
                </div>
                <!-- end followup-questions-container for Solar Lending -->
              }

              <!-- Energy Efficiency Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you offer Energy Efficiency Products or services?
                  </h3>
                  @if (
                    isMandatoryField(
                      "solar_and_energy.offers_energy_efficiency"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.solar_and_energy
                        ?.offers_energy_efficiency
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <!-- Battery Lending Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you offer lending products to obtain a battery?
                  </h3>
                  @if (
                    isMandatoryField("solar_and_energy.offers_battery_lending")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.solar_and_energy
                        ?.offers_battery_lending
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <!-- Battery Loan Details + follow-ups - Only shown when Battery Lending is Yes -->
              @if (
                savedCriteria.criteria.solar_and_energy?.offers_battery_lending
              ) {
                <div class="followup-questions-container">
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.battery_loan_amount_range"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <div class="loan-range-display">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.battery_loan_amount_range
                        ) {
                          <div class="loan-amounts">
                            <span class="amount-label">Minimum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.battery_loan_amount_range?.min
                                  | number: "1.0-0"
                              }}</span
                            >
                            <span class="amount-separator">-</span>
                            <span class="amount-label">Maximum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.battery_loan_amount_range?.max
                                  | number: "1.0-0"
                              }}</span
                            >
                          </div>
                        } @else {
                          <span class="no-data">Not specified</span>
                        }
                      </div>
                    </div>
                  </div>

                  <!-- Battery Credit Score Question -->
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.battery_need_credit_score"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <span class="name-text">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.battery_need_credit_score
                        ) {
                          Yes
                        } @else {
                          No
                        }
                      </span>
                    </div>
                  </div>

                  <!-- Battery Credit Score Ranges - Only shown when Credit Score is Yes -->
                  @if (
                    savedCriteria.criteria.solar_and_energy
                      ?.battery_need_credit_score
                  ) {
                    <div class="followup-questions-container">
                      <div class="view-section">
                        <div class="section-title-with-badge">
                          <h3 class="section-title">Credit score range:</h3>
                          @if (
                            isMandatoryField(
                              "solar_and_energy.battery_credit_score_ranges"
                            )
                          ) {
                            <span class="must-match-badge">Must Match</span>
                          }
                        </div>
                        <div class="view-content">
                          <div class="tag-list">
                            @if (getBatteryCreditScoreRange()) {
                              <span class="view-tag">{{
                                getBatteryCreditScoreRange()
                              }}</span>
                            } @else {
                              <span class="no-data">Not specified</span>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- Battery Loan Requirements Question -->
                  <div class="view-section">
                    <h3 class="section-title">
                      Do you require anything else for a Battery loan?
                    </h3>
                    <div class="view-content">
                      @if (
                        savedCriteria.criteria.solar_and_energy
                          ?.battery_loan_requirements
                      ) {
                        <span class="name-text">
                          {{
                            savedCriteria.criteria.solar_and_energy
                              ?.battery_loan_requirements
                          }}
                        </span>
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </div>
                  </div>
                </div>
                <!-- end followup-questions-container for Battery Lending -->
              }

              <!-- Power Use Statement Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you need a building power-use statement for either solar
                    or battery lending products?
                  </h3>
                  @if (
                    isMandatoryField(
                      "solar_and_energy.need_power_use_statement"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.solar_and_energy
                        ?.need_power_use_statement
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <!-- EV Products Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    Do you offer products to help obtain electric fleet or
                    regular vehicles?
                  </h3>
                  @if (
                    isMandatoryField("solar_and_energy.offers_ev_products")
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.solar_and_energy
                        ?.offers_ev_products
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>

              <!-- EV Loan Details + follow-ups - Only shown when EV Products is Yes -->
              @if (
                savedCriteria.criteria.solar_and_energy?.offers_ev_products
              ) {
                <div class="followup-questions-container">
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.ev_loan_amount_range"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <div class="loan-range-display">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.ev_loan_amount_range
                        ) {
                          <div class="loan-amounts">
                            <span class="amount-label">Minimum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.ev_loan_amount_range?.min | number: "1.0-0"
                              }}</span
                            >
                            <span class="amount-separator">-</span>
                            <span class="amount-label">Maximum:</span>
                            <span class="amount-value"
                              >${{
                                savedCriteria.criteria.solar_and_energy
                                  ?.ev_loan_amount_range?.max | number: "1.0-0"
                              }}</span
                            >
                          </div>
                        } @else {
                          <span class="no-data">Not specified</span>
                        }
                      </div>
                    </div>
                  </div>

                  <!-- EV Credit Score Question -->
                  <div class="view-section">
                    <div class="section-title-with-badge">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      @if (
                        isMandatoryField(
                          "solar_and_energy.ev_need_credit_score"
                        )
                      ) {
                        <span class="must-match-badge">Must Match</span>
                      }
                    </div>
                    <div class="view-content">
                      <span class="name-text">
                        @if (
                          savedCriteria.criteria.solar_and_energy
                            ?.ev_need_credit_score
                        ) {
                          Yes
                        } @else {
                          No
                        }
                      </span>
                    </div>
                  </div>

                  <!-- EV Credit Score Ranges - Only shown when Credit Score is Yes -->
                  @if (
                    savedCriteria.criteria.solar_and_energy
                      ?.ev_need_credit_score
                  ) {
                    <div class="followup-questions-container">
                      <div class="view-section">
                        <div class="section-title-with-badge">
                          <h3 class="section-title">Credit score range:</h3>
                          @if (
                            isMandatoryField(
                              "solar_and_energy.ev_credit_score_ranges"
                            )
                          ) {
                            <span class="must-match-badge">Must Match</span>
                          }
                        </div>
                        <div class="view-content">
                          <div class="tag-list">
                            @if (getEvCreditScoreRange()) {
                              <span class="view-tag">{{
                                getEvCreditScoreRange()
                              }}</span>
                            } @else {
                              <span class="no-data">Not specified</span>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  }

                  <!-- EV Loan Requirements Question -->
                  <div class="view-section">
                    <h3 class="section-title">
                      Do you require anything else for an Electric Vehicle Loan?
                    </h3>
                    <div class="view-content">
                      @if (
                        savedCriteria.criteria.solar_and_energy
                          ?.ev_loan_requirements
                      ) {
                        <span class="name-text">
                          {{
                            savedCriteria.criteria.solar_and_energy
                              ?.ev_loan_requirements
                          }}
                        </span>
                      } @else {
                        <span class="no-data">Not specified</span>
                      }
                    </div>
                  </div>
                </div>
                <!-- end followup-questions-container for EV Products -->
              }
            </div>
          </div>
        }

        <!-- Investing in Companies Section - Only shown when selected -->
        @if (savedCriteria.criteria.supports.includes("finding_investors")) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Investing in Companies
              </h3>

              <!-- Investment Types Question -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    What type of business investing can you offer or connect a
                    business to?
                  </h3>
                  @if (isMandatoryField("finding_investors.investment_types")) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                @if (getInvestmentTypesArray().length > 0) {
                  <div class="view-content">
                    <div class="tag-list">
                      @for (type of getInvestmentTypesArray(); track $index) {
                        <span class="view-tag">{{ type }}</span>
                      }
                    </div>
                  </div>
                } @else {
                  <div class="view-content">
                    <span class="no-data">Not specified</span>
                  </div>
                }
              </div>

              <!-- Regional Investment Requirement -->
              <div class="view-section">
                <div class="section-title-with-badge">
                  <h3 class="section-title">
                    If you are the investor, do you require the investment to be
                    in your region?
                  </h3>
                  @if (
                    isMandatoryField(
                      "finding_investors.require_regional_investment"
                    )
                  ) {
                    <span class="must-match-badge">Must Match</span>
                  }
                </div>
                <div class="view-content">
                  <span class="name-text">
                    @if (
                      savedCriteria.criteria.finding_investors
                        ?.require_regional_investment
                    ) {
                      Yes
                    } @else {
                      No
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Edit Mode -->
  @if (isEditMode) {
    <div class="matching-criteria-container">
      <form
        [formGroup]="matchingCriteriaForm"
        (ngSubmit)="onSave()"
        class="matching-criteria-form"
      >
        <!-- Name -->
        <div class="form-section">
          <h3 class="section-title">Name</h3>
          <input
            type="text"
            formControlName="name"
            class="form-input"
            placeholder="Enter criteria name"
            [class.error]="
              matchingCriteriaForm.get('name')?.invalid &&
              matchingCriteriaForm.get('name')?.touched
            "
          />
          @if (
            matchingCriteriaForm.get("name")?.hasError("required") &&
            matchingCriteriaForm.get("name")?.touched
          ) {
            <div class="error-message">Name is required</div>
          }
          @if (
            matchingCriteriaForm.get("name")?.hasError("blank") &&
            matchingCriteriaForm.get("name")?.touched
          ) {
            <div class="error-message">
              Name cannot be blank or contain only spaces
            </div>
          }
        </div>

        <!-- Description -->
        <div class="form-section">
          <h3 class="section-title optional">Description</h3>
          <textarea
            formControlName="description"
            class="form-textarea"
            placeholder="Enter description (optional)"
            rows="3"
          ></textarea>
        </div>

        <!-- Supported Languages -->
        <div class="form-section">
          <div class="section-title-with-must-match">
            <h3 class="section-title">Supported Languages</h3>
            <label
              class="must-match-checkbox"
              [class.disabled]="isMustMatchDisabled('preferred_languages')"
            >
              <input
                type="checkbox"
                [checked]="isMandatoryField('preferred_languages')"
                [disabled]="isMustMatchDisabled('preferred_languages')"
                (change)="toggleMandatoryField('preferred_languages')"
              />
              <span class="must-match-label">Must Match</span>
            </label>
          </div>
          <div class="checkbox-group checkbox-grid">
            @for (language of supportedLanguageOptions; track $index) {
              <label
                class="checkbox-item"
                [class.selected]="
                  isOptionSelected('supportedLanguages', language)
                "
              >
                <input
                  type="checkbox"
                  [checked]="isOptionSelected('supportedLanguages', language)"
                  (change)="onCheckboxToggle('supportedLanguages', language)"
                />
                <span class="checkmark"></span>
                {{ language }}
              </label>
            }
          </div>
          @if (
            supportedLanguagesArray.hasError("atLeastOneSelected") &&
            supportedLanguagesArray.touched
          ) {
            <div class="error-message">
              Please select at least one supported language
            </div>
          }
        </div>

        <!-- Nationwide Support -->
        <div class="form-section">
          <h3 class="section-title">Do you provide services nationwide?</h3>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                formControlName="nationwideSupport"
                value="yes"
              />
              <span class="radio-checkmark"></span>
              <span class="radio-label">Yes</span>
            </label>
            <label class="radio-item">
              <input
                type="radio"
                formControlName="nationwideSupport"
                value="no"
              />
              <span class="radio-checkmark"></span>
              <span class="radio-label">No</span>
            </label>
          </div>
          @if (
            matchingCriteriaForm
              .get("nationwideSupport")
              ?.hasError("required") &&
            matchingCriteriaForm.get("nationwideSupport")?.touched
          ) {
            <div class="error-message">Please select an option</div>
          }
        </div>

        <!-- States of Service -->
        @if (!isNationwideSupport()) {
          <div class="form-section">
            <div class="section-header">
              <div class="section-title-with-must-match">
                <h3 class="section-title">States of Service</h3>
                <label
                  class="must-match-checkbox"
                  [class.disabled]="isMustMatchDisabled('states')"
                >
                  <input
                    type="checkbox"
                    [checked]="isMandatoryField('states')"
                    [disabled]="isMustMatchDisabled('states')"
                    (change)="toggleMandatoryField('states')"
                  />
                  <span class="must-match-label">Must Match</span>
                </label>
              </div>
              <div class="search-input-container">
                <input
                  type="text"
                  placeholder="Search states..."
                  class="search-input"
                  [value]="stateSearchTerm"
                  (input)="onStateSearchChange($event)"
                />
                @if (stateSearchTerm) {
                  <button
                    type="button"
                    class="clear-search-btn"
                    (click)="clearStateSearch()"
                    title="Clear search"
                  >
                    ×
                  </button>
                }
              </div>
            </div>
            <div class="checkbox-group states-grid">
              @for (state of filteredStates; track $index) {
                <label
                  class="checkbox-item"
                  [class.selected]="isStateSelected(state)"
                >
                  <input
                    type="checkbox"
                    [checked]="isStateSelected(state)"
                    (change)="onStateToggle(state)"
                  />
                  <span class="checkmark"></span>
                  {{ state }}
                </label>
              }
              @if (filteredStates.length === 0) {
                <div class="no-results-message">
                  No states found matching "{{ stateSearchTerm }}"
                </div>
              }
            </div>
            @if (
              statesOfServiceArray.hasError("required") &&
              statesOfServiceArray.touched
            ) {
              <div class="error-message">
                Please select at least one state of service
              </div>
            }
          </div>
        }

        <!-- Counties of Service -->
        @if (!isNationwideSupport()) {
          <div class="form-section">
            <div class="section-header">
              <div class="section-title-with-must-match">
                <h3 class="section-title">Counties of Service</h3>
                <label
                  class="must-match-checkbox"
                  [class.disabled]="isMustMatchDisabled('counties')"
                >
                  <input
                    type="checkbox"
                    [checked]="isMandatoryField('counties')"
                    [disabled]="isMustMatchDisabled('counties')"
                    (change)="toggleMandatoryField('counties')"
                  />
                  <span class="must-match-label">Must Match</span>
                </label>
              </div>
              @if (availableCounties.length > 0) {
                <div class="search-input-container">
                  <input
                    type="text"
                    placeholder="Search counties..."
                    class="search-input"
                    [value]="countySearchTerm"
                    (input)="onCountySearchChange($event)"
                  />
                  @if (countySearchTerm) {
                    <button
                      type="button"
                      class="clear-search-btn"
                      (click)="clearCountySearch()"
                      title="Clear search"
                    >
                      ×
                    </button>
                  }
                </div>
              }
            </div>
            @if (availableCounties.length === 0) {
              <div class="info-message">
                Please select one or more states to see available counties.
              </div>
            } @else {
              <div class="select-all-section">
                <label
                  class="checkbox-item select-all-item"
                  [class.selected]="areAllCountiesSelected()"
                >
                  <input
                    type="checkbox"
                    [checked]="areAllCountiesSelected()"
                    [indeterminate]="
                      areSomeCountiesSelected() && !areAllCountiesSelected()
                    "
                    (change)="onSelectAllCounties($event)"
                  />
                  <span class="checkmark"></span>
                  Select All Counties
                </label>
              </div>
              <div class="checkbox-group checkbox-grid counties-grid">
                @for (county of filteredCounties; track $index) {
                  <label
                    class="checkbox-item"
                    [class.selected]="
                      isOptionSelected('countiesOfService', county)
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="isOptionSelected('countiesOfService', county)"
                      (change)="onCheckboxToggle('countiesOfService', county)"
                    />
                    <span class="checkmark"></span>
                    {{ county }}
                  </label>
                }
                @if (filteredCounties.length === 0) {
                  <div class="no-results-message">
                    No counties found matching "{{ countySearchTerm }}"
                  </div>
                }
              </div>
            }
            @if (
              countiesOfServiceArray.hasError("atLeastOneSelected") &&
              countiesOfServiceArray.touched
            ) {
              <div class="error-message">
                Please select at least one county of service
              </div>
            }
          </div>
        }

        <!-- Specific Cities/Townships -->
        @if (!isNationwideSupport()) {
          <div class="form-section">
            <div class="section-header">
              <div class="section-title-with-must-match">
                <h3 class="section-title optional">
                  Specific Cities/Townships
                </h3>
                <label
                  class="must-match-checkbox"
                  [class.disabled]="isMustMatchDisabled('cities')"
                >
                  <input
                    type="checkbox"
                    [checked]="isMandatoryField('cities')"
                    [disabled]="isMustMatchDisabled('cities')"
                    (change)="toggleMandatoryField('cities')"
                  />
                  <span class="must-match-label">Must Match</span>
                </label>
              </div>
              @if (cities.length > 0) {
                <div class="search-input-container">
                  <input
                    type="text"
                    placeholder="Search cities..."
                    class="search-input"
                    [value]="citySearchTerm"
                    (input)="onCitySearchChange($event)"
                  />
                  @if (citySearchTerm) {
                    <button
                      type="button"
                      class="clear-search-btn"
                      (click)="clearCitySearch()"
                      title="Clear search"
                    >
                      ×
                    </button>
                  }
                </div>
              }
            </div>

            @if (cities.length === 0) {
              <div class="info-message">
                Please select one or more counties to see available cities.
              </div>
            } @else {
              <div class="checkbox-group checkbox-grid counties-grid">
                @for (city of filteredCities; track city) {
                  <label
                    class="checkbox-item"
                    [class.selected]="isOptionSelected('cities', city)"
                  >
                    <input
                      type="checkbox"
                      [checked]="isOptionSelected('cities', city)"
                      (change)="onCityToggle(city)"
                    />
                    <span class="checkmark"></span>
                    {{ formatCityName(city) }}
                  </label>
                }
              </div>
            }
          </div>
        }

        <!-- Zip Codes -->
        @if (!isNationwideSupport()) {
          <div class="form-section">
            <div class="section-header">
              <div class="section-title-with-must-match">
                <h3 class="section-title optional">Zip Codes</h3>
                <label
                  class="must-match-checkbox"
                  [class.disabled]="isMustMatchDisabled('zip_codes')"
                >
                  <input
                    type="checkbox"
                    [checked]="isMandatoryField('zip_codes')"
                    [disabled]="isMustMatchDisabled('zip_codes')"
                    (change)="toggleMandatoryField('zip_codes')"
                  />
                  <span class="must-match-label">Must Match</span>
                </label>
              </div>
              @if (zipcodes.length > 0) {
                <div class="search-input-container">
                  <input
                    type="text"
                    placeholder="Search zip codes..."
                    class="search-input"
                    [value]="zipSearchTerm"
                    (input)="onZipSearchChange($event)"
                  />
                  @if (zipSearchTerm) {
                    <button
                      type="button"
                      class="clear-search-btn"
                      (click)="clearZipSearch()"
                      title="Clear search"
                    >
                      ×
                    </button>
                  }
                </div>
              }
            </div>

            @if (zipcodes.length === 0) {
              <div class="info-message">
                Please select a city to see available zip codes.
              </div>
            } @else {
              <div class="checkbox-group checkbox-grid counties-grid">
                @for (zip of filteredZipcodes; track zip) {
                  <label
                    class="checkbox-item"
                    [class.selected]="isOptionSelected('zipCodes', zip)"
                  >
                    <input
                      type="checkbox"
                      [checked]="isOptionSelected('zipCodes', zip)"
                      (change)="onCheckboxToggle('zipCodes', zip)"
                    />
                    <span class="checkmark"></span>
                    {{ zip }}
                  </label>
                }
              </div>
            }
          </div>
        }

        <!--Military Program -->
        <div class="form-section">
          <div class="section-title-with-must-match">
            <h3 class="section-title">
              Is this program for US Military Veterans?
            </h3>
            <label
              class="must-match-checkbox"
              [class.disabled]="isMustMatchDisabled('is_military_veteran_prog')"
            >
              <input
                type="checkbox"
                [checked]="isMandatoryField('is_military_veteran_prog')"
                [disabled]="isMustMatchDisabled('is_military_veteran_prog')"
                (change)="toggleMandatoryField('is_military_veteran_prog')"
              />
              <span class="must-match-label">Must Match</span>
            </label>
          </div>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                formControlName="isMilitaryProgram"
                value="yes"
              />
              <span class="radio-checkmark"></span>
              Yes
            </label>
            <label class="radio-item">
              <input
                type="radio"
                formControlName="isMilitaryProgram"
                value="no"
              />
              <span class="radio-checkmark"></span>
              No
            </label>
          </div>
          @if (
            matchingCriteriaForm
              .get("isMilitaryProgram")
              ?.hasError("required") &&
            matchingCriteriaForm.get("isMilitaryProgram")?.touched
          ) {
            <div class="error-message">Please select an option</div>
          }
        </div>

        <!--Military Veteran Spouse -->
        <div class="form-section">
          <div class="section-title-with-must-match">
            <h3 class="section-title">
              Is this program for spouses of US military veterans?
            </h3>
            <label
              class="must-match-checkbox"
              [class.disabled]="isMustMatchDisabled('is_veteran_spouse_prog')"
            >
              <input
                type="checkbox"
                [checked]="isMandatoryField('is_veteran_spouse_prog')"
                [disabled]="isMustMatchDisabled('is_veteran_spouse_prog')"
                (change)="toggleMandatoryField('is_veteran_spouse_prog')"
              />
              <span class="must-match-label">Must Match</span>
            </label>
          </div>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                formControlName="isVeteranSpouse"
                value="yes"
              />
              <span class="radio-checkmark"></span>
              Yes
            </label>
            <label class="radio-item">
              <input
                type="radio"
                formControlName="isVeteranSpouse"
                value="no"
              />
              <span class="radio-checkmark"></span>
              No
            </label>
          </div>
          @if (
            matchingCriteriaForm.get("isVeteranSpouse")?.hasError("required") &&
            matchingCriteriaForm.get("isVeteranSpouse")?.touched
          ) {
            <div class="error-message">Please select an option</div>
          }
        </div>

        <!-- Business Support Options -->
        <div class="form-section">
          <div class="section-title-with-must-match">
            <h3 class="section-title">
              From this list, what can you support a business with?
            </h3>
            <label
              class="must-match-checkbox"
              [class.disabled]="isMustMatchDisabled('supports')"
            >
              <input
                type="checkbox"
                [checked]="isMandatoryField('supports')"
                [disabled]="isMustMatchDisabled('supports')"
                (change)="toggleMandatoryField('supports')"
              />
              <span class="must-match-label">Must Match</span>
            </label>
          </div>
          <div class="checkbox-group checkbox-grid">
            @for (option of businessSupportOptions; track $index) {
              <label
                class="checkbox-item"
                [class.selected]="isOptionSelected('businessSupport', option)"
              >
                <input
                  type="checkbox"
                  [checked]="isOptionSelected('businessSupport', option)"
                  (change)="onCheckboxToggle('businessSupport', option)"
                />
                <span class="checkmark"></span>
                {{ option }}
              </label>
            }
          </div>
          @if (
            businessSupportArray.hasError("atLeastOneSelected") &&
            businessSupportArray.touched
          ) {
            <div class="error-message">
              Please select at least one business support option
            </div>
          }
        </div>
        <!-- Business Entity Requirement -->
        <div class="form-section">
          <div class="section-title-with-must-match">
            <h3 class="section-title">
              Does there need to be a business entity to lend or deploy capital
              to?
            </h3>
            <label
              class="must-match-checkbox"
              [class.disabled]="isMustMatchDisabled('need_business_entity')"
            >
              <input
                type="checkbox"
                [checked]="isMandatoryField('need_business_entity')"
                [disabled]="isMustMatchDisabled('need_business_entity')"
                (change)="toggleMandatoryField('need_business_entity')"
              />
              <span class="must-match-label">Must Match</span>
            </label>
          </div>
          <div class="radio-group">
            <label class="radio-item">
              <input
                type="radio"
                formControlName="needBusinessEntity"
                value="yes"
              />
              <span class="radio-checkmark"></span>
              Yes
            </label>
            <label class="radio-item">
              <input
                type="radio"
                formControlName="needBusinessEntity"
                value="no"
              />
              <span class="radio-checkmark"></span>
              No
            </label>
          </div>
          @if (
            matchingCriteriaForm
              .get("needBusinessEntity")
              ?.hasError("required") &&
            matchingCriteriaForm.get("needBusinessEntity")?.touched
          ) {
            <div class="error-message">Please select an option</div>
          }
        </div>
        <!-- Loans and Capital Products Section - Only shown when selected -->
        @if (isLoansAndCapitalSelected()) {
          <div class="loans-capital-section">
            <div class="section-header-main">
              <h3 class="section-title-main">Loans and Capital Products</h3>
            </div>
            <div class="section-content">
              <!-- State Registration Requirement -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Does the business need to have a state registration?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.need_state_registration'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'loans_and_capital.need_state_registration'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.need_state_registration'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.need_state_registration'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needStateRegistration"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needStateRegistration"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("needStateRegistration")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("needStateRegistration")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Co-signer Requirement -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Does a co-signer help the prospect of a loan deployment?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('loans_and_capital.cosigner_helps')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('loans_and_capital.cosigner_helps')
                      "
                      [disabled]="
                        isMustMatchDisabled('loans_and_capital.cosigner_helps')
                      "
                      (change)="
                        toggleMandatoryField('loans_and_capital.cosigner_helps')
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="cosignerHelps"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="cosignerHelps"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="cosignerHelps"
                      value="not_required"
                    />
                    <span class="radio-checkmark"></span>
                    Not Required
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("cosignerHelps")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("cosignerHelps")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Business Plan Requirement -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Does an applicant need a business plan for this product?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.need_business_plan'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('loans_and_capital.need_business_plan')
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.need_business_plan'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.need_business_plan'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needBusinessPlan"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needBusinessPlan"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needBusinessPlan"
                      value="yes_we_can_help"
                    />
                    <span class="radio-checkmark"></span>
                    Yes, and we can help create one for an applicant
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("needBusinessPlan")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("needBusinessPlan")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Financial Projections Requirement -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you need financial projections for this product?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.need_financial_projections'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'loans_and_capital.need_financial_projections'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.need_financial_projections'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.need_financial_projections'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needFinancialProjections"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needFinancialProjections"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needFinancialProjections"
                      value="yes_we_can_help"
                    />
                    <span class="radio-checkmark"></span>
                    Yes, and we can help create one for an applicant
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("needFinancialProjections")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("needFinancialProjections")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Area Types -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you have funds to deploy in any of these area types:
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.capital_deployment_areas'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'loans_and_capital.capital_deployment_areas'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.capital_deployment_areas'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.capital_deployment_areas'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  @for (areaType of areaTypeOptions; track $index) {
                    <label
                      class="checkbox-item"
                      [class.selected]="isOptionSelected('areaTypes', areaType)"
                    >
                      <input
                        type="checkbox"
                        [checked]="isOptionSelected('areaTypes', areaType)"
                        (change)="onCheckboxToggle('areaTypes', areaType)"
                      />
                      <span class="checkmark"></span>
                      {{ areaType }}
                    </label>
                  }
                </div>
                @if (
                  areaTypesArray.hasError("atLeastOneSelected") &&
                  areaTypesArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one area type
                  </div>
                }
              </div>

              <!-- Loan Types -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">What loan types do you offer?</h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('loans_and_capital.loan_types')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('loans_and_capital.loan_types')
                      "
                      [disabled]="
                        isMustMatchDisabled('loans_and_capital.loan_types')
                      "
                      (change)="
                        toggleMandatoryField('loans_and_capital.loan_types')
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  @for (loanType of loanTypeOptions; track $index) {
                    <label
                      class="checkbox-item"
                      [class.selected]="isOptionSelected('loanTypes', loanType)"
                    >
                      <input
                        type="checkbox"
                        [checked]="isOptionSelected('loanTypes', loanType)"
                        (change)="onCheckboxToggle('loanTypes', loanType)"
                      />
                      <span class="checkmark"></span>
                      {{ loanType }}
                    </label>
                  }
                </div>
                @if (
                  loanTypesArray.hasError("atLeastOneSelected") &&
                  loanTypesArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one loan type
                  </div>
                }
              </div>
              <!-- Loan Amount -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Offering Loan amount range in USD
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('loans_and_capital.loan_size_range')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('loans_and_capital.loan_size_range')
                      "
                      [disabled]="
                        isMustMatchDisabled('loans_and_capital.loan_size_range')
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.loan_size_range'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="loan-inputs-container">
                  <div>
                    <label class="to-label" for="loanMinAmount"
                      >Minimum amount:</label
                    >
                    <input
                      type="number"
                      formControlName="loanMinAmount"
                      id="loanMinAmount"
                      class="form-input loan-input"
                      [class.error]="
                        matchingCriteriaForm.get('loanMinAmount')?.invalid &&
                        matchingCriteriaForm.get('loanMinAmount')?.touched
                      "
                    />
                    @if (getErrorMessage("loanMinAmount")) {
                      <div class="error-message">
                        {{ getErrorMessage("loanMinAmount") }}
                      </div>
                    }
                  </div>
                  <div>
                    <label class="to-label" for="loanMaxAmount"
                      >Maximum amount:</label
                    >
                    <input
                      type="number"
                      formControlName="loanMaxAmount"
                      id="loanMaxAmount"
                      class="form-input loan-input"
                      [class.error]="
                        matchingCriteriaForm.get('loanMaxAmount')?.invalid &&
                        matchingCriteriaForm.get('loanMaxAmount')?.touched
                      "
                    />
                    @if (getErrorMessage("loanMaxAmount")) {
                      <div class="error-message">
                        {{ getErrorMessage("loanMaxAmount") }}
                      </div>
                    }
                  </div>
                </div>
                @if (
                  matchingCriteriaForm.hasError("invalidLoanRange") &&
                  (matchingCriteriaForm.get("loanMinAmount")?.touched ||
                    matchingCriteriaForm.get("loanMaxAmount")?.touched)
                ) {
                  <div class="error-message">
                    Maximum loan amount must be greater than minimum loan amount
                  </div>
                }
              </div>
              <!-- Financial Documentation -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you require borrowers to have financial documentation
                    like the documents listed below?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.financial_documentations'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'loans_and_capital.financial_documentations'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.financial_documentations'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.financial_documentations'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  @for (doc of financialDocOptions; track $index) {
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected('financialDocumentation', doc)
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected('financialDocumentation', doc)
                        "
                        (change)="
                          onCheckboxToggle('financialDocumentation', doc)
                        "
                      />
                      <span class="checkmark"></span>
                      {{ doc }}
                    </label>
                  }
                </div>
                @if (
                  financialDocArray.hasError("atLeastOneSelected") &&
                  financialDocArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one financial documentation option
                  </div>
                }
              </div>
              <!-- Credit Score Range -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    What Credit Score Range can you support?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'loans_and_capital.credit_score_range'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('loans_and_capital.credit_score_range')
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'loans_and_capital.credit_score_range'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'loans_and_capital.credit_score_range'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  @for (range of creditScoreOptions; track $index) {
                    <label class="radio-item">
                      <input
                        type="radio"
                        formControlName="creditScoreRange"
                        [value]="range"
                      />
                      <span class="radio-checkmark"></span>
                      {{ range }}
                    </label>
                  }
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creditScoreRange")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creditScoreRange")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>
            </div>
          </div>
        }
        <!-- Small Business Technical Assistance Section - Only shown when selected -->
        @if (isTechnicalAssistanceSelected()) {
          <div class="loans-capital-section">
            <div class="section-header-main">
              <h3 class="section-title-main">
                Small Business Technical Assistance
              </h3>
            </div>
            <div class="section-content">
              <!-- Target Industries Question -->
              <div class="form-section">
                <h3 class="section-title">
                  Do you have any target or preferred business industries?
                </h3>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="hasTargetIndustries"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="hasTargetIndustries"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("hasTargetIndustries")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("hasTargetIndustries")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Target Industries Selection - Only shown when Yes -->
              @if (
                matchingCriteriaForm.get("hasTargetIndustries")?.value === "yes"
              ) {
                <div class="form-section">
                  <div class="section-header">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">Select Target Industries</h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'technical_assistance.target_industries'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'technical_assistance.target_industries'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'technical_assistance.target_industries'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'technical_assistance.target_industries'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="search-input-container">
                      <input
                        type="text"
                        placeholder="Search industries..."
                        class="search-input"
                        [value]="industrySearchTerm"
                        (input)="onIndustrySearchChange($event)"
                      />
                      @if (industrySearchTerm) {
                        <button
                          type="button"
                          class="clear-search-btn"
                          (click)="clearIndustrySearch()"
                          title="Clear search"
                        >
                          ×
                        </button>
                      }
                    </div>
                  </div>
                  <div class="checkbox-group checkbox-grid">
                    @for (
                      industry of filteredIndustries;
                      track industry.value
                    ) {
                      <label
                        class="checkbox-item"
                        [class.selected]="isIndustrySelected(industry.value)"
                      >
                        <input
                          type="checkbox"
                          [checked]="isIndustrySelected(industry.value)"
                          (change)="onIndustryToggle(industry.value)"
                        />
                        <span class="checkmark"></span>
                        {{ industry.label }}
                      </label>
                    }
                    @if (filteredIndustries.length === 0) {
                      <div class="no-results-message">
                        No industries found matching "{{ industrySearchTerm }}"
                      </div>
                    }
                  </div>
                  @if (
                    targetIndustriesArray.hasError("atLeastOneSelected") &&
                    targetIndustriesArray.touched
                  ) {
                    <div class="error-message">
                      Please select at least one industry of service
                    </div>
                  }
                </div>
              }

              <!-- Support Areas Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer Small Business support in any of the following
                    areas? We can help with (Select any):
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('technical_assistance.support_areas')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('technical_assistance.support_areas')
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'technical_assistance.support_areas'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'technical_assistance.support_areas'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  @for (area of supportAreaOptions; track $index) {
                    <label
                      class="checkbox-item"
                      [class.selected]="isOptionSelected('supportAreas', area)"
                    >
                      <input
                        type="checkbox"
                        [checked]="isOptionSelected('supportAreas', area)"
                        (change)="onCheckboxToggle('supportAreas', area)"
                      />
                      <span class="checkmark"></span>
                      {{ area }}
                    </label>
                  }
                </div>
                @if (
                  supportAreasArray.hasError("atLeastOneSelected") &&
                  supportAreasArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one area of service
                  </div>
                }
              </div>

              <!-- Employee Ownership Question - Only shown when Business Succession Planning is selected -->
              @if (isBusinessSuccessionSelected()) {
                <div class="form-section">
                  <h3 class="section-title">
                    Do you offer programming in Employee Ownership transitions?
                  </h3>
                  <div class="radio-group">
                    <label class="radio-item">
                      <input
                        type="radio"
                        formControlName="offersEmployeeOwnership"
                        value="yes"
                      />
                      <span class="radio-checkmark"></span>
                      Yes
                    </label>
                    <label class="radio-item">
                      <input
                        type="radio"
                        formControlName="offersEmployeeOwnership"
                        value="no"
                      />
                      <span class="radio-checkmark"></span>
                      No
                    </label>
                  </div>
                  @if (
                    matchingCriteriaForm
                      .get("offersEmployeeOwnership")
                      ?.hasError("required") &&
                    matchingCriteriaForm.get("offersEmployeeOwnership")?.touched
                  ) {
                    <div class="error-message">Please select an option</div>
                  }
                </div>
              }

              <!-- TA Programs Paid Service Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Are your technical assistance programs a paid service?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'technical_assistance.ta_programs_paid_service'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'technical_assistance.ta_programs_paid_service'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'technical_assistance.ta_programs_paid_service'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'technical_assistance.ta_programs_paid_service'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="taProgramsPaidService"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="taProgramsPaidService"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="taProgramsPaidService"
                      value="some"
                    />
                    <span class="radio-checkmark"></span>
                    Some of them
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("taProgramsPaidService")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("taProgramsPaidService")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Paid Support Areas - Only shown when Some of them is selected -->
              @if (isTaProgramsSomeOfThem()) {
                <div class="form-section">
                  <h3 class="section-title">
                    Which services do you offer that are paid?
                  </h3>
                  <div class="checkbox-group checkbox-grid">
                    @for (area of selectedSupportAreas; track $index) {
                      <label
                        class="checkbox-item"
                        [class.selected]="
                          isOptionSelected('paidSupportAreas', area)
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="isOptionSelected('paidSupportAreas', area)"
                          (change)="onCheckboxToggle('paidSupportAreas', area)"
                        />
                        <span class="checkmark"></span>
                        {{ area }}
                      </label>
                    }
                    @if (selectedSupportAreas.length === 0) {
                      <div class="no-results-message">
                        Please select support areas first
                      </div>
                    }
                  </div>
                  @if (
                    paidSupportAreasArray.hasError("atLeastOneSelected") &&
                    paidSupportAreasArray.touched
                  ) {
                    <div class="error-message">
                      Please select at least one service
                    </div>
                  }
                </div>
              }

              <!-- Business Help Areas Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Can you help a business with any of the following?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'technical_assistance.business_help_areas'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'technical_assistance.business_help_areas'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'technical_assistance.business_help_areas'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'technical_assistance.business_help_areas'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  @for (area of businessHelpAreaOptions; track $index) {
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected('businessHelpAreas', area)
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="isOptionSelected('businessHelpAreas', area)"
                        (change)="onCheckboxToggle('businessHelpAreas', area)"
                      />
                      <span class="checkmark"></span>
                      {{ area }}
                    </label>
                  }
                </div>
                @if (
                  businessHelpAreasArray.hasError("atLeastOneSelected") &&
                  businessHelpAreasArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one help of service
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Business Operational Tools Section - Only shown when selected -->
        @if (isBusinessOperationalToolsSelected()) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Business Operational Tools
              </h3>

              <!-- Operational Tools Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Does your organization offer any of the following tools or
                    services?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('business_operational_tools.tools')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('business_operational_tools.tools')
                      "
                      [disabled]="
                        isMustMatchDisabled('business_operational_tools.tools')
                      "
                      (change)="
                        toggleMandatoryField('business_operational_tools.tools')
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>

                <!-- Business Banking -->
                <div class="checkbox-group-with-header">
                  <h4 class="checkbox-group-header">Business Banking</h4>
                  <div class="checkbox-group checkbox-grid">
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Open or manage business accounts (checking, savings, investments)'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Open or manage business accounts (checking, savings, investments)'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Open or manage business accounts (checking, savings, investments)'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Open or manage business accounts (checking, savings,
                      investments)
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help with payroll and HR'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help with payroll and HR'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help with payroll and HR'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help with payroll and HR
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Get a line of credit'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Get a line of credit'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Get a line of credit'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Get a line of credit
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help managing your company\'s cash flow'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help managing your company\'s cash flow'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help managing your company\'s cash flow'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help managing your company's cash flow
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Advice on financial strategy'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Advice on financial strategy'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Advice on financial strategy'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Advice on financial strategy
                    </label>
                  </div>
                </div>

                <!-- Marketing Help -->
                <div class="checkbox-group-with-header">
                  <h4 class="checkbox-group-header">Marketing Help</h4>
                  <div class="checkbox-group checkbox-grid">
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help designing your website or improving search engine visibility'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help designing your website or improving search engine visibility'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help designing your website or improving search engine visibility'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help designing your website or improving search engine
                      visibility
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help with flyers, signs, or other physical marketing'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help with flyers, signs, or other physical marketing'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help with flyers, signs, or other physical marketing'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help with flyers, signs, or other physical marketing
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help planning or improving your marketing strategy'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help planning or improving your marketing strategy'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help planning or improving your marketing strategy'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help planning or improving your marketing strategy
                    </label>
                  </div>
                </div>

                <!-- Tech Support -->
                <div class="checkbox-group-with-header">
                  <h4 class="checkbox-group-header">Tech Support</h4>
                  <div class="checkbox-group checkbox-grid">
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help with IT setup, troubleshooting, and online security'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help with IT setup, troubleshooting, and online security'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help with IT setup, troubleshooting, and online security'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help with IT setup, troubleshooting, and online security
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help setting up physical tech like servers, internet, etc.'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help setting up physical tech like servers, internet, etc.'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help setting up physical tech like servers, internet, etc.'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help setting up physical tech like servers, internet, etc.
                    </label>
                  </div>
                </div>

                <!-- Business Insurance -->
                <div class="checkbox-group-with-header">
                  <h4 class="checkbox-group-header">Business Insurance</h4>
                  <div class="checkbox-group checkbox-grid">
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'General business insurance'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'General business insurance'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'General business insurance'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      General business insurance
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Insurance for disasters (flood, fire, etc.)'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Insurance for disasters (flood, fire, etc.)'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Insurance for disasters (flood, fire, etc.)'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Insurance for disasters (flood, fire, etc.)
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Property or building insurance'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Property or building insurance'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Property or building insurance'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Property or building insurance
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Protection from lawsuits'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Protection from lawsuits'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Protection from lawsuits'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Protection from lawsuits
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Insurance for key team members'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Insurance for key team members'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Insurance for key team members'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Insurance for key team members
                    </label>
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Coverage for directors or officers'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Coverage for directors or officers'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Coverage for directors or officers'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Coverage for directors or officers
                    </label>
                  </div>
                </div>

                <!-- New Technology Tools -->
                <div class="checkbox-group-with-header">
                  <h4 class="checkbox-group-header">New Technology Tools</h4>
                  <div class="checkbox-group checkbox-grid">
                    <label
                      class="checkbox-item"
                      [class.selected]="
                        isOptionSelected(
                          'operationalTools',
                          'Help using new digital tools in your business'
                        )
                      "
                    >
                      <input
                        type="checkbox"
                        [checked]="
                          isOptionSelected(
                            'operationalTools',
                            'Help using new digital tools in your business'
                          )
                        "
                        (change)="
                          onCheckboxToggle(
                            'operationalTools',
                            'Help using new digital tools in your business'
                          )
                        "
                      />
                      <span class="checkmark"></span>
                      Help using new digital tools in your business
                    </label>
                  </div>
                </div>

                @if (
                  operationalToolsArray.hasError("atLeastOneSelected") &&
                  operationalToolsArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one tool or service
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Commercial Real Estate Section - Only shown when selected -->
        @if (isCommercialRealEstateSelected()) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Commercial Real Estate
              </h3>

              <!-- CRE Help Locate Facilities Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you help locate CRE facilities for a business to purchase
                    or rent?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'commercial_real_estate.cre_help_locate_facilities'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'commercial_real_estate.cre_help_locate_facilities'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'commercial_real_estate.cre_help_locate_facilities'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'commercial_real_estate.cre_help_locate_facilities'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creHelpLocateFacilities"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creHelpLocateFacilities"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creHelpLocateFacilities")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creHelpLocateFacilities")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- CRE Upgrade Facility Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you have a product or service that helps a business
                    upgrade its commercial facility?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'commercial_real_estate.cre_upgrade_facility'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'commercial_real_estate.cre_upgrade_facility'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'commercial_real_estate.cre_upgrade_facility'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'commercial_real_estate.cre_upgrade_facility'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creUpgradeFacility"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creUpgradeFacility"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creUpgradeFacility")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creUpgradeFacility")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- CRE Need Own Facility Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Does the business need to own their facility to receive your
                    product/service?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'commercial_real_estate.cre_need_own_facility'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'commercial_real_estate.cre_need_own_facility'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'commercial_real_estate.cre_need_own_facility'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'commercial_real_estate.cre_need_own_facility'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creNeedOwnFacility"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creNeedOwnFacility"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creNeedOwnFacility")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creNeedOwnFacility")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- CRE Work With Tenants Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you work with tenants to connect with building ownership
                    to improve/upgrade the building?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'commercial_real_estate.cre_work_with_tenants'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'commercial_real_estate.cre_work_with_tenants'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'commercial_real_estate.cre_work_with_tenants'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'commercial_real_estate.cre_work_with_tenants'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creWorkWithTenants"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creWorkWithTenants"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creWorkWithTenants")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creWorkWithTenants")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- CRE Solar Energy Upgrades Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer building Solar or Energy Efficiency upgrades
                    products or services?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'commercial_real_estate.cre_solar_energy_upgrades'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'commercial_real_estate.cre_solar_energy_upgrades'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'commercial_real_estate.cre_solar_energy_upgrades'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'commercial_real_estate.cre_solar_energy_upgrades'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creSolarEnergyUpgrades"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="creSolarEnergyUpgrades"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("creSolarEnergyUpgrades")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("creSolarEnergyUpgrades")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Solar and Energy Efficiency Products Section - Only shown when selected -->
        @if (isSolarEnergySelected()) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h2 class="section-header-main">
                Solar and Energy Efficiency Products
              </h2>

              <!-- Solar Lending Products Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer Solar Lending Products or Solar Support
                    Services?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'solar_and_energy.solar_lending_products'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'solar_and_energy.solar_lending_products'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'solar_and_energy.solar_lending_products'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'solar_and_energy.solar_lending_products'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="solarLendingProducts"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="solarLendingProducts"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("solarLendingProducts")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("solarLendingProducts")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Loan Amount - Only shown when Solar Lending is Yes -->
              @if (isSolarLendingYes()) {
                <div class="followup-questions-container">
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.solar_loan_amount_range'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.solar_loan_amount_range'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.solar_loan_amount_range'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.solar_loan_amount_range'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="loan-amount-inputs">
                      <div class="input-group">
                        <label for="solarLoanMinAmount">Min Amount ($)</label>
                        <input
                          type="number"
                          id="solarLoanMinAmount"
                          formControlName="solarLoanMinAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('solarLoanMinAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('solarLoanMinAmount')
                              ?.touched
                          "
                        />
                        @if (getErrorMessage("solarLoanMinAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("solarLoanMinAmount") }}
                          </div>
                        }
                      </div>
                      <div class="input-group">
                        <label for="solarLoanMaxAmount">Max Amount ($)</label>
                        <input
                          type="number"
                          id="solarLoanMaxAmount"
                          formControlName="solarLoanMaxAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('solarLoanMaxAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('solarLoanMaxAmount')
                              ?.touched
                          "
                        />
                        @if (getErrorMessage("solarLoanMaxAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("solarLoanMaxAmount") }}
                          </div>
                        }
                      </div>
                    </div>
                    @if (
                      matchingCriteriaForm.hasError("invalidSolarLoanRange") &&
                      (matchingCriteriaForm.get("solarLoanMinAmount")
                        ?.touched ||
                        matchingCriteriaForm.get("solarLoanMaxAmount")?.touched)
                    ) {
                      <div class="error-message">
                        Maximum loan amount must be greater than minimum loan
                        amount
                      </div>
                    }
                  </div>

                  <!-- Solar Credit Score Question -->
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.solar_need_credit_score'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.solar_need_credit_score'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.solar_need_credit_score'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.solar_need_credit_score'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="radio-group">
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="solarNeedCreditScore"
                          value="yes"
                        />
                        <span class="radio-checkmark"></span>
                        Yes
                      </label>
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="solarNeedCreditScore"
                          value="no"
                        />
                        <span class="radio-checkmark"></span>
                        No
                      </label>
                    </div>
                    @if (
                      matchingCriteriaForm
                        .get("solarNeedCreditScore")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("solarNeedCreditScore")?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>

                  <!-- Solar Credit Score Range - Only shown when Credit Score is Yes -->
                  @if (isSolarNeedCreditScoreYes()) {
                    <div class="followup-questions-container">
                      <div class="form-section">
                        <div class="section-title-with-must-match">
                          <h3 class="section-title">
                            Select credit score range:
                          </h3>
                          <label
                            class="must-match-checkbox"
                            [class.disabled]="
                              isMustMatchDisabled(
                                'solar_and_energy.solar_credit_score_ranges'
                              )
                            "
                          >
                            <input
                              type="checkbox"
                              [checked]="
                                isMandatoryField(
                                  'solar_and_energy.solar_credit_score_ranges'
                                )
                              "
                              [disabled]="
                                isMustMatchDisabled(
                                  'solar_and_energy.solar_credit_score_ranges'
                                )
                              "
                              (change)="
                                toggleMandatoryField(
                                  'solar_and_energy.solar_credit_score_ranges'
                                )
                              "
                            />
                            <span class="must-match-label">Must Match</span>
                          </label>
                        </div>
                        <div class="radio-group">
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="solarCreditScoreRange"
                              value="Below 580"
                            />
                            <span class="radio-checkmark"></span>
                            Below 580
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="solarCreditScoreRange"
                              value="580 and above"
                            />
                            <span class="radio-checkmark"></span>
                            580 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="solarCreditScoreRange"
                              value="670 and above"
                            />
                            <span class="radio-checkmark"></span>
                            670 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="solarCreditScoreRange"
                              value="740 and above"
                            />
                            <span class="radio-checkmark"></span>
                            740 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="solarCreditScoreRange"
                              value="800 and above"
                            />
                            <span class="radio-checkmark"></span>
                            800 and above
                          </label>
                        </div>
                        @if (
                          matchingCriteriaForm
                            .get("solarCreditScoreRange")
                            ?.hasError("required") &&
                          matchingCriteriaForm.get("solarCreditScoreRange")
                            ?.touched
                        ) {
                          <div class="error-message">
                            Please select an option
                          </div>
                        }
                      </div>
                    </div>
                    <!-- end nested credit score followup -->
                  }

                  <!-- Solar Loan Requirements Question -->
                  <div class="form-section">
                    <h3 class="section-title">
                      Do you require anything else for a Solar Loan?
                    </h3>
                    <textarea
                      formControlName="solarLoanRequirements"
                      rows="4"
                      placeholder="Enter any additional requirements..."
                      class="form-textarea"
                    ></textarea>
                    @if (
                      matchingCriteriaForm
                        .get("solarLoanRequirements")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("solarLoanRequirements")?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>
                </div>
                <!-- end followup-questions-container for Solar Lending -->
              }

              <!-- Energy Efficiency Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer Energy Efficiency Products or services?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'solar_and_energy.offers_energy_efficiency'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'solar_and_energy.offers_energy_efficiency'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'solar_and_energy.offers_energy_efficiency'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'solar_and_energy.offers_energy_efficiency'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersEnergyEfficiency"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersEnergyEfficiency"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("offersEnergyEfficiency")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("offersEnergyEfficiency")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Battery Lending Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer lending products to obtain a battery?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'solar_and_energy.offers_battery_lending'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'solar_and_energy.offers_battery_lending'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'solar_and_energy.offers_battery_lending'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'solar_and_energy.offers_battery_lending'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersBatteryLending"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersBatteryLending"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("offersBatteryLending")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("offersBatteryLending")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- Battery Loan Details - Only shown when Battery Lending is Yes -->
              @if (isBatteryLendingYes()) {
                <div class="followup-questions-container">
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.battery_loan_amount_range'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.battery_loan_amount_range'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.battery_loan_amount_range'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.battery_loan_amount_range'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="loan-amount-inputs">
                      <div class="input-group">
                        <label for="batteryLoanMinAmount">Min Amount ($)</label>
                        <input
                          type="number"
                          id="batteryLoanMinAmount"
                          formControlName="batteryLoanMinAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('batteryLoanMinAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('batteryLoanMinAmount')
                              ?.touched
                          "
                        />
                        @if (getErrorMessage("batteryLoanMinAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("batteryLoanMinAmount") }}
                          </div>
                        }
                      </div>
                      <div class="input-group">
                        <label for="batteryLoanMaxAmount">Max Amount ($)</label>
                        <input
                          type="number"
                          id="batteryLoanMaxAmount"
                          formControlName="batteryLoanMaxAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('batteryLoanMaxAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('batteryLoanMaxAmount')
                              ?.touched
                          "
                        />
                        @if (getErrorMessage("batteryLoanMaxAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("batteryLoanMaxAmount") }}
                          </div>
                        }
                      </div>
                    </div>
                    @if (
                      matchingCriteriaForm.hasError(
                        "invalidBatteryLoanRange"
                      ) &&
                      (matchingCriteriaForm.get("batteryLoanMinAmount")
                        ?.touched ||
                        matchingCriteriaForm.get("batteryLoanMaxAmount")
                          ?.touched)
                    ) {
                      <div class="error-message">
                        Maximum loan amount must be greater than minimum loan
                        amount
                      </div>
                    }
                  </div>

                  <!-- Battery Credit Score Question -->
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.battery_need_credit_score'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.battery_need_credit_score'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.battery_need_credit_score'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.battery_need_credit_score'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="radio-group">
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="batteryNeedCreditScore"
                          value="yes"
                        />
                        <span class="radio-checkmark"></span>
                        Yes
                      </label>
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="batteryNeedCreditScore"
                          value="no"
                        />
                        <span class="radio-checkmark"></span>
                        No
                      </label>
                    </div>
                    @if (
                      matchingCriteriaForm
                        .get("batteryNeedCreditScore")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("batteryNeedCreditScore")
                        ?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>

                  <!-- Battery Credit Score Range - Only shown when Credit Score is Yes -->
                  @if (isBatteryNeedCreditScoreYes()) {
                    <div class="followup-questions-container">
                      <div class="form-section">
                        <div class="section-title-with-must-match">
                          <h3 class="section-title">
                            Select credit score range:
                          </h3>
                          <label
                            class="must-match-checkbox"
                            [class.disabled]="
                              isMustMatchDisabled(
                                'solar_and_energy.battery_credit_score_ranges'
                              )
                            "
                          >
                            <input
                              type="checkbox"
                              [checked]="
                                isMandatoryField(
                                  'solar_and_energy.battery_credit_score_ranges'
                                )
                              "
                              [disabled]="
                                isMustMatchDisabled(
                                  'solar_and_energy.battery_credit_score_ranges'
                                )
                              "
                              (change)="
                                toggleMandatoryField(
                                  'solar_and_energy.battery_credit_score_ranges'
                                )
                              "
                            />
                            <span class="must-match-label">Must Match</span>
                          </label>
                        </div>
                        <div class="radio-group">
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="batteryCreditScoreRange"
                              value="Below 580"
                            />
                            <span class="radio-checkmark"></span>
                            Below 580
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="batteryCreditScoreRange"
                              value="580 and above"
                            />
                            <span class="radio-checkmark"></span>
                            580 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="batteryCreditScoreRange"
                              value="670 and above"
                            />
                            <span class="radio-checkmark"></span>
                            670 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="batteryCreditScoreRange"
                              value="740 and above"
                            />
                            <span class="radio-checkmark"></span>
                            740 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="batteryCreditScoreRange"
                              value="800 and above"
                            />
                            <span class="radio-checkmark"></span>
                            800 and above
                          </label>
                        </div>
                        @if (
                          matchingCriteriaForm
                            .get("batteryCreditScoreRange")
                            ?.hasError("required") &&
                          matchingCriteriaForm.get("batteryCreditScoreRange")
                            ?.touched
                        ) {
                          <div class="error-message">
                            Please select an option
                          </div>
                        }
                      </div>
                    </div>
                    <!-- end nested credit score followup -->
                  }

                  <!-- Battery Loan Requirements Question -->
                  <div class="form-section">
                    <h3 class="section-title">
                      Do you require anything else for a Battery loan?
                    </h3>
                    <textarea
                      formControlName="batteryLoanRequirements"
                      rows="4"
                      placeholder="Enter any additional requirements..."
                      class="form-textarea"
                    ></textarea>
                    @if (
                      matchingCriteriaForm
                        .get("batteryLoanRequirements")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("batteryLoanRequirements")
                        ?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>
                </div>
                <!-- end followup-questions-container for Battery Lending -->
              }

              <!-- Power Use Statement Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you need a building power-use statement for either solar
                    or battery lending products?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'solar_and_energy.need_power_use_statement'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'solar_and_energy.need_power_use_statement'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'solar_and_energy.need_power_use_statement'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'solar_and_energy.need_power_use_statement'
                        )
                      "
                    />
                    Must Match
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needPowerUseStatement"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="needPowerUseStatement"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("needPowerUseStatement")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("needPowerUseStatement")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- EV Products Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    Do you offer products to help obtain electric fleet or
                    regular vehicles?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('solar_and_energy.offers_ev_products')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('solar_and_energy.offers_ev_products')
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'solar_and_energy.offers_ev_products'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'solar_and_energy.offers_ev_products'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersEvProducts"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="offersEvProducts"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("offersEvProducts")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("offersEvProducts")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>

              <!-- EV Loan Details - Only shown when EV Products is Yes -->
              @if (isEvProductsYes()) {
                <div class="followup-questions-container">
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        What is the min/max loan amount?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.ev_loan_amount_range'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.ev_loan_amount_range'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.ev_loan_amount_range'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.ev_loan_amount_range'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="loan-amount-inputs">
                      <div class="input-group">
                        <label for="evLoanMinAmount">Min Amount ($)</label>
                        <input
                          type="number"
                          id="evLoanMinAmount"
                          formControlName="evLoanMinAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('evLoanMinAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('evLoanMinAmount')?.touched
                          "
                        />
                        @if (getErrorMessage("evLoanMinAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("evLoanMinAmount") }}
                          </div>
                        }
                      </div>
                      <div class="input-group">
                        <label for="evLoanMaxAmount">Max Amount ($)</label>
                        <input
                          type="number"
                          id="evLoanMaxAmount"
                          formControlName="evLoanMaxAmount"
                          min="0"
                          [class.error]="
                            matchingCriteriaForm.get('evLoanMaxAmount')
                              ?.invalid &&
                            matchingCriteriaForm.get('evLoanMaxAmount')?.touched
                          "
                        />
                        @if (getErrorMessage("evLoanMaxAmount")) {
                          <div class="error-message">
                            {{ getErrorMessage("evLoanMaxAmount") }}
                          </div>
                        }
                      </div>
                    </div>
                    @if (
                      matchingCriteriaForm.hasError("invalidEvLoanRange") &&
                      (matchingCriteriaForm.get("evLoanMinAmount")?.touched ||
                        matchingCriteriaForm.get("evLoanMaxAmount")?.touched)
                    ) {
                      <div class="error-message">
                        Maximum loan amount must be greater than minimum loan
                        amount
                      </div>
                    }
                  </div>

                  <!-- EV Credit Score Question -->
                  <div class="form-section">
                    <div class="section-title-with-must-match">
                      <h3 class="section-title">
                        Do you need a credit score to participate?
                      </h3>
                      <label
                        class="must-match-checkbox"
                        [class.disabled]="
                          isMustMatchDisabled(
                            'solar_and_energy.ev_need_credit_score'
                          )
                        "
                      >
                        <input
                          type="checkbox"
                          [checked]="
                            isMandatoryField(
                              'solar_and_energy.ev_need_credit_score'
                            )
                          "
                          [disabled]="
                            isMustMatchDisabled(
                              'solar_and_energy.ev_need_credit_score'
                            )
                          "
                          (change)="
                            toggleMandatoryField(
                              'solar_and_energy.ev_need_credit_score'
                            )
                          "
                        />
                        <span class="must-match-label">Must Match</span>
                      </label>
                    </div>
                    <div class="radio-group">
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="evNeedCreditScore"
                          value="yes"
                        />
                        <span class="radio-checkmark"></span>
                        Yes
                      </label>
                      <label class="radio-item">
                        <input
                          type="radio"
                          formControlName="evNeedCreditScore"
                          value="no"
                        />
                        <span class="radio-checkmark"></span>
                        No
                      </label>
                    </div>
                    @if (
                      matchingCriteriaForm
                        .get("evNeedCreditScore")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("evNeedCreditScore")?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>

                  <!-- EV Credit Score Range - Only shown when Credit Score is Yes -->
                  @if (isEvNeedCreditScoreYes()) {
                    <div class="followup-questions-container">
                      <div class="form-section">
                        <div class="section-title-with-must-match">
                          <h3 class="section-title">
                            Select credit score range:
                          </h3>
                          <label
                            class="must-match-checkbox"
                            [class.disabled]="
                              isMustMatchDisabled(
                                'solar_and_energy.ev_credit_score_ranges'
                              )
                            "
                          >
                            <input
                              type="checkbox"
                              [checked]="
                                isMandatoryField(
                                  'solar_and_energy.ev_credit_score_ranges'
                                )
                              "
                              [disabled]="
                                isMustMatchDisabled(
                                  'solar_and_energy.ev_credit_score_ranges'
                                )
                              "
                              (change)="
                                toggleMandatoryField(
                                  'solar_and_energy.ev_credit_score_ranges'
                                )
                              "
                            />
                            <span class="must-match-label">Must Match</span>
                          </label>
                        </div>
                        <div class="radio-group">
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="evCreditScoreRange"
                              value="Below 580"
                            />
                            <span class="radio-checkmark"></span>
                            Below 580
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="evCreditScoreRange"
                              value="580 and above"
                            />
                            <span class="radio-checkmark"></span>
                            580 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="evCreditScoreRange"
                              value="670 and above"
                            />
                            <span class="radio-checkmark"></span>
                            670 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="evCreditScoreRange"
                              value="740 and above"
                            />
                            <span class="radio-checkmark"></span>
                            740 and above
                          </label>
                          <label class="radio-item">
                            <input
                              type="radio"
                              formControlName="evCreditScoreRange"
                              value="800 and above"
                            />
                            <span class="radio-checkmark"></span>
                            800 and above
                          </label>
                        </div>
                        @if (
                          matchingCriteriaForm
                            .get("evCreditScoreRange")
                            ?.hasError("required") &&
                          matchingCriteriaForm.get("evCreditScoreRange")
                            ?.touched
                        ) {
                          <div class="error-message">
                            Please select an option
                          </div>
                        }
                      </div>
                    </div>
                    <!-- end nested credit score followup -->
                  }

                  <!-- EV Loan Requirements Question -->
                  <div class="form-section">
                    <h3 class="section-title">
                      Do you require anything else for an Electric Vehicle Loan?
                    </h3>
                    <textarea
                      formControlName="evLoanRequirements"
                      rows="4"
                      placeholder="Enter any additional requirements..."
                      class="form-textarea"
                    ></textarea>
                    @if (
                      matchingCriteriaForm
                        .get("evLoanRequirements")
                        ?.hasError("required") &&
                      matchingCriteriaForm.get("evLoanRequirements")?.touched
                    ) {
                      <div class="error-message">Please select an option</div>
                    }
                  </div>
                </div>
                <!-- end followup-questions-container for EV Products -->
              }
            </div>
          </div>
        }

        <!-- Investing in Companies Section - Only shown when selected -->
        @if (isInvestingInCompaniesSelected()) {
          <div class="loans-capital-section">
            <div class="section-content">
              <h3 class="section-header-main" style="margin: 0px">
                Investing in Companies
              </h3>

              <!-- Investment Types Question -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    What type of business investing can you offer or connect a
                    business to?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled('finding_investors.investment_types')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField('finding_investors.investment_types')
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'finding_investors.investment_types'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'finding_investors.investment_types'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="checkbox-group checkbox-grid">
                  <label
                    class="checkbox-item"
                    [class.selected]="
                      isOptionSelected('investmentTypes', 'Equity Investing')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isOptionSelected('investmentTypes', 'Equity Investing')
                      "
                      (change)="
                        onCheckboxToggle('investmentTypes', 'Equity Investing')
                      "
                    />
                    <span class="checkmark"></span>
                    Equity Investing
                  </label>
                  <label
                    class="checkbox-item"
                    [class.selected]="
                      isOptionSelected('investmentTypes', 'Debt Investing')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isOptionSelected('investmentTypes', 'Debt Investing')
                      "
                      (change)="
                        onCheckboxToggle('investmentTypes', 'Debt Investing')
                      "
                    />
                    <span class="checkmark"></span>
                    Debt Investing
                  </label>
                  <label
                    class="checkbox-item"
                    [class.selected]="
                      isOptionSelected('investmentTypes', 'Revenue Sharing')
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isOptionSelected('investmentTypes', 'Revenue Sharing')
                      "
                      (change)="
                        onCheckboxToggle('investmentTypes', 'Revenue Sharing')
                      "
                    />
                    <span class="checkmark"></span>
                    Revenue Sharing
                  </label>
                </div>
                @if (
                  investmentTypesArray.hasError("atLeastOneSelected") &&
                  investmentTypesArray.touched
                ) {
                  <div class="error-message">
                    Please select at least one investment of service
                  </div>
                }
              </div>

              <!-- Regional Investment Requirement -->
              <div class="form-section">
                <div class="section-title-with-must-match">
                  <h3 class="section-title">
                    If you are the investor, do you require the investment to be
                    in your region?
                  </h3>
                  <label
                    class="must-match-checkbox"
                    [class.disabled]="
                      isMustMatchDisabled(
                        'finding_investors.require_regional_investment'
                      )
                    "
                  >
                    <input
                      type="checkbox"
                      [checked]="
                        isMandatoryField(
                          'finding_investors.require_regional_investment'
                        )
                      "
                      [disabled]="
                        isMustMatchDisabled(
                          'finding_investors.require_regional_investment'
                        )
                      "
                      (change)="
                        toggleMandatoryField(
                          'finding_investors.require_regional_investment'
                        )
                      "
                    />
                    <span class="must-match-label">Must Match</span>
                  </label>
                </div>
                <div class="radio-group">
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="requireRegionalInvestment"
                      value="yes"
                    />
                    <span class="radio-checkmark"></span>
                    Yes
                  </label>
                  <label class="radio-item">
                    <input
                      type="radio"
                      formControlName="requireRegionalInvestment"
                      value="no"
                    />
                    <span class="radio-checkmark"></span>
                    No
                  </label>
                </div>
                @if (
                  matchingCriteriaForm
                    .get("requireRegionalInvestment")
                    ?.hasError("required") &&
                  matchingCriteriaForm.get("requireRegionalInvestment")?.touched
                ) {
                  <div class="error-message">Please select an option</div>
                }
              </div>
            </div>
          </div>
        }

        <!-- Save Button -->
        <div class="form-actions">
          @if (saveApiValidationMessages.length > 0) {
            <div class="form-validation-message api-validation-message">
              <strong>Please fix the following:</strong>
              <ul class="api-validation-list">
                @for (message of saveApiValidationMessages; track $index) {
                  <li>{{ message }}</li>
                }
              </ul>
            </div>
          }
          @if (matchingCriteriaForm.invalid) {
            <div class="form-validation-message">
              @if (hasEmptyEntries()) {
                <span>
                  Please fill in or remove any empty city or zip code fields
                  before saving.
                  <br />
                  <button
                    type="button"
                    class="cleanup-button"
                    (click)="cleanupEmptyEntriesManually()"
                  >
                    Remove all empty fields
                  </button>
                </span>
              }
              @if (!hasEmptyEntries()) {
                <span>
                  Please complete all required fields to save your matching
                  criteria.
                </span>
              }
            </div>
          }
          <div class="action-buttons">
            @if (savedCriteria) {
              <button type="button" class="cancel-button" (click)="onCancel()">
                Cancel
              </button>
            }
            <button type="submit" class="save-button">Save</button>
          </div>
        </div>
      </form>
    </div>
  }

  <!-- Back Confirmation Popup -->
  @if (showBackConfirmPopup) {
    <app-popup
      heading="You have unsaved changes"
      confirmText="Leave"
      cancelText="Stay"
      (confirmClicked)="onBackConfirmed()"
      (cancelClicked)="onBackCancelled()"
    >
      <p>Are you sure you want to leave? All changes will be lost.</p>
    </app-popup>
  }
}
